import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { prettyLocalDate } from '../../../lib/time';
import { chooseIcon } from './ChooseIcon';
import Checkbox from '@material-ui/core/Checkbox';
import DocumentItemActions from './DocumentItemActions';
import Grid from '@material-ui/core/Grid';
import DocumentDetailDialogue from './DocumentDetailDialogue';
import DocumentCategoryChip from './DocumentCategoryChip';
import * as track from '../../../lib/track';

const useStyles = makeStyles(theme => ({
  root: {
    padding: '0.5rem 0',
    width: '100%',
    borderBottom: `1px solid ${theme.palette.common.paleGrey}`,
    '&:hover': {
      backgroundColor: theme.palette.common.paleGrey,
    },
  },
  container: {
    padding: 0,
  },
  nameCell: {
    padding: '0.2rem 0.3rem 0.2rem 0.4rem',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  leftCell: {
    padding: '0.2rem 0.3rem 0.2rem 0',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      padding: '0.4rem 1rem',
    },
  },
  centerCell: {
    padding: '0.2rem 0',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      padding: '0.4rem 1rem',
      justifyContent: 'flex-start',
    },
  },
  name: {
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: 400,
    '&:hover': {
      textDecoration: 'underline',
    },
    [theme.breakpoints.down('sm')]: {
      paddingLeft: '1rem',
    },
  },
  text: {
    fontSize: '0.8rem',
    '&:first-letter': {
      textTransform: 'capitalize',
    },
  },
  docIcon: {
    fontSize: '1.7rem',
    margin: '0 0.5rem',
  },
  chip: {
    borderRadius: '4px',
    width: '8rem',
    padding: '0.8rem 0.2rem',
    borderWidth: '3px',
    margin: 0,
  },
  checkbox: {
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '&:after': {
      backgroundColor: 'transparent',
    },
  },
}));

const DocumentItem = ({data, checkFile, downloadFile, renameFile, deleteFile, setFileCategory}) => {
  const classes = useStyles();
  const [detailOpen, setDetailOpen] = useState(false);
  const from = (data.origin_name === null) ? data.origin : data.origin_name;

  const openDetail = () => {
    track.sendEvent('file item name', 'click', {uuid: data.uuid});
    setDetailOpen(true);
  };

  const closeDetail = () => setDetailOpen(false);

  return (
    <div key={data.uuid} className={classes.root}>
      <Grid container spacing={0} className={classes.container}>
        <Grid item sm={2} md={2} lg={1} className={classes.centerCell}>
          <Checkbox disableRipple color="primary" className={classes.checkbox} onChange={e=>checkFile(e, data)}/>
          {chooseIcon(data.content_type, classes.docIcon)}
        </Grid>
        <Grid item sm={10} md={8} lg={4} className={classes.nameCell}>
          <Typography variant="body2" className={classes.name} noWrap onClick={openDetail}>{data.name}</Typography>
        </Grid>
        <Grid item sm={12} md={2} lg={2} className={classes.leftCell}>
          <Typography variant="body2" className={classes.text} noWrap>{prettyLocalDate(data.created_at)}</Typography>
        </Grid>
        <Grid item sm={12} md={4} lg={2} className={classes.leftCell}>
          <Typography variant="body2" className={classes.text} noWrap>{from}</Typography>
        </Grid>
        <Grid item sm={4} md={4} lg={2} className={classes.centerCell}>
          <DocumentCategoryChip
            data={data}
            editable={true}
            setFileCategory={setFileCategory}
          />
        </Grid>
        <Grid item sm={8} md={4} lg={1} className={classes.centerCell}>
          <DocumentItemActions
            data={data}
            downloadFile={downloadFile}
            renameFile={renameFile}
            deleteFile={deleteFile}
          />
        </Grid>
      </Grid>
      <DocumentDetailDialogue
        open={detailOpen}
        data={data}
        downloadFile={downloadFile}
        onClose={closeDetail}
      />
    </div>
  );
};

DocumentItem.propTypes = {
  data: PropTypes.object.isRequired,
  checkFile: PropTypes.func.isRequired,
  downloadFile: PropTypes.func.isRequired,
  renameFile: PropTypes.func.isRequired,
  deleteFile: PropTypes.func.isRequired,
  setFileCategory: PropTypes.func.isRequired,
};

export default DocumentItem;
